import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search as SearchIcon, X, ShoppingBag, Filter, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { AddToCartButton } from '../components/Cart';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOPIFY_STORE = 'https://gifts.charmingcreationsgifts.com';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');

  // Fetch all products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/shopify/products?limit=250`);
        if (response.data.products) {
          setProducts(response.data.products);
        }
      } catch (err) {
        console.error('Failed to fetch products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    let result = products;
    
    if (query.trim()) {
      const searchTerms = query.toLowerCase().split(' ');
      result = products.filter(product => {
        const searchableText = `${product.name} ${product.description || ''} ${(product.tags || []).join(' ')}`.toLowerCase();
        return searchTerms.every(term => searchableText.includes(term));
      });
    }
    
    // Apply sorting
    return result.sort((a, b) => {
      if (sortBy === 'newest') {
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA;
      }
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      // Default to newest
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return dateB - dateA;
    });
  }, [products, query, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <Helmet>
        <title>{query ? `Search: ${query}` : 'Search Products'} | Charming Creations</title>
        <meta name="description" content="Search our collection of handcrafted resin art jewellery, earrings, pendants, and homewares. Find your perfect piece at Charming Creations." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-200 shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                alt="Charming Creations Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-lg font-bold bg-gradient-to-r from-amber-600 to-teal-600 bg-clip-text text-transparent hidden sm:block">
                Charming Creations
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-amber-300 hover:bg-amber-50">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Search Section */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for earrings, pendants, homewares..."
                className="pl-12 pr-12 py-6 text-lg border-2 border-gray-200 focus:border-amber-500 rounded-xl"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); setSearchParams({}); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          {/* Results Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              {query ? (
                <h1 className="text-2xl font-bold text-gray-900">
                  {loading ? 'Searching...' : `${filteredProducts.length} results for "${query}"`}
                </h1>
              ) : (
                <h1 className="text-2xl font-bold text-gray-900">
                  {loading ? 'Loading products...' : `All Products (${products.length})`}
                </h1>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              >
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name">Name: A to Z</option>
              </select>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, idx) => (
                <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">No products found</h2>
              <p className="text-gray-600 mb-6">
                Try different keywords or browse our collections
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                <Link to="/earrings"><Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer">Earrings</Badge></Link>
                <Link to="/pendants"><Badge className="bg-teal-100 text-teal-800 hover:bg-teal-200 cursor-pointer">Pendants</Badge></Link>
                <Link to="/homewares"><Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200 cursor-pointer">Homewares</Badge></Link>
                <Link to="/keycharms"><Badge className="bg-pink-100 text-pink-800 hover:bg-pink-200 cursor-pointer">Key Charms</Badge></Link>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredProducts.map((product, idx) => (
                <ProductCard key={idx} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product }) => {
  const [isHovered, setIsHovered] = useState(false);
  const productUrl = `/product/${product.handle}`;
  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;

  return (
    <div 
      className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all border border-gray-100"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={productUrl}>
        <div className="relative aspect-square overflow-hidden rounded-t-xl bg-gray-50">
          <img 
            src={product.image || product.images?.[0]}
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-300 ${isHovered ? 'scale-110' : 'scale-100'}`}
            loading="lazy"
          />
          {hasDiscount && (
            <Badge className="absolute top-2 left-2 bg-red-500 text-white text-xs">
              {Math.round((1 - product.price / product.compare_at_price) * 100)}% OFF
            </Badge>
          )}
          {!product.inStock && (
            <Badge className="absolute top-2 right-2 bg-gray-800 text-white text-xs">Sold Out</Badge>
          )}
        </div>
      </Link>
      <div className="p-4">
        <Link to={productUrl}>
          <h3 className="font-medium text-gray-900 text-sm line-clamp-2 min-h-[40px] group-hover:text-amber-600 transition-colors">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-amber-600">${product.price?.toFixed(2)}</span>
            {hasDiscount && (
              <span className="text-sm text-gray-400 line-through">${product.compare_at_price?.toFixed(2)}</span>
            )}
          </div>
          <AddToCartButton product={product} variant="small" />
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
